import { useState } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { roundAmount2Dig } from "utils/amount";
import {
  exclusiveToInclusive,
  getItemNetRate,
  withItemDirectDiscount,
  withItemDiscountPercentage,
  withSyncedRatesFromExclusive,
  withSyncedRatesFromInclusive,
} from "utils/salebillRate";
import ItemSelectSizeGrouped from "components/item/ItemSelectSizeGrouped";
import { useItemDetailsWithSizes } from "http-hooks/agitem/useAgitem";
import { useItemsTaxAndPriceListDetails } from "http-hooks/agitemdetail.js/useAgitemdetail";
import { useBill } from "hooks/bill";
import useAuth from "hooks/useAuth";
import useCompanyPreferences from "hooks/useCompanyPreferences";
import Input from "components/ui/Input";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  ActionButton,
} from "components/ui/Table";

export default function DirStkTable({ docType }) {
  const [rowCaseNo, setRowCaseNo] = useState(1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemSizes, setItemSizes] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    selectBoxes,
    tableData,
    regularTableData,
    setTableData,
    onUpdateItemQty,
    removeTableItemsRow,
    isFromSchemeIssue,
    hasApplicableGst,
    companyHasGst,
    isForeignCurrencyBill,
  } = useBill();
  const { agttp } = useAuth();
  const { isSizeEnabled } = useCompanyPreferences();
  const sizeEnabled = isSizeEnabled();

  const vendid = selectBoxes.vendor?.ps_Id;
  const requiresVendor = agttp === 0;
  const itemVendorId = requiresVendor ? vendid : null;
  const plRateId = selectBoxes.pricelist?.rateid;

  const { mutateAsync: fetchItemSizes } = useItemDetailsWithSizes();
  const { mutateAsync: fetchitemsWithTaxAndPricelistMutate } =
    useItemsTaxAndPriceListDetails({});

  const addItemsFromDetails = async (selected, sizeRows) => {
    const itemids = sizeRows
      .filter((sz) => sz.qty > 0)
      .map((sz) => sz.agitemdtlId);
    if (!itemids.length) return;

    const data = await fetchitemsWithTaxAndPricelistMutate({
      pl_rate_id: plRateId,
      itemids,
    });

    const currentOrdno = selectBoxes.billType?.value || "STK";
    const currentCaseno = rowCaseNo;
    const existingRecord = tableData?.find(
      (record) =>
        record.ordno === currentOrdno && record.caseno === currentCaseno
    );
    const smartLotQty = existingRecord ? existingRecord.slotqty : 1;

    const mappedData = data.map((row) => {
      const sizeInfo = sizeRows.find((sz) => sz.agitemdtlId === row.agitemdtlId);
      return {
        selected: true,
        ordno: currentOrdno,
        caseno: currentCaseno,
        slotqty: smartLotQty,
        sqty: sizeInfo ? sizeInfo.qty : 0,
        srate: isForeignCurrencyBill ? 0 : row.rate || 0,
        srate_incl: hasApplicableGst
          ? exclusiveToInclusive(row.rate || 0, row.tax || 0)
          : isForeignCurrencyBill ? 0 : row.rate || 0,
        dis1: 0,
        dis2perc: 0,
        dis2: 0,
        tax: hasApplicableGst ? row.tax || 0 : 0,
        item: {
          itemid: row.agitemdtlId,
          item_full_name: row.item_full_name || selected?.label || "",
          aguomconval: selected?.conval || 1,
          itmhsnId: selected?.itmhsnId || row.itmhsnId || "",
          itemuom: selected?.itemuom || "",
        },
        source: currentOrdno,
        isAdditionalItems: false,
      };
    });

    onUpdateItemQty(mappedData);
    setSelectedItem(null);
    setItemSizes([]);
  };

  const handleSelectedItem = async (val) => {
    if (!val) {
      setSelectedItem(null);
      setItemSizes([]);
      return;
    }

    // Size pref off: skip size table and add the item directly to the bill grid
    if (!sizeEnabled) {
      try {
        setIsUpdating(true);
        const sizes = await fetchItemSizes(val.value);
        if (!sizes?.length) return;
        await addItemsFromDetails(val, [{ ...sizes[0], qty: 1 }]);
      } catch (error) {
        console.error("Error adding item:", error);
      } finally {
        setIsUpdating(false);
      }
      return;
    }

    setSelectedItem(val);
    const sizes = await fetchItemSizes(val.value);
    const sizesWithQty = (sizes || []).map((s) => ({ ...s, qty: 0 }));
    setItemSizes(sizesWithQty);
  };

  const handleItemRowSizeQtyChange = (index, val) => {
    setItemSizes((prev) => [
      ...prev.slice(0, index),
      { ...prev[index], qty: parseFloat(val) || 0 },
      ...prev.slice(index + 1),
    ]);
  };

  const handleLotChange = (changedRow, newLotValue) => {
    const newData = tableData?.map((row) =>
      row.ordno === changedRow.ordno && row.caseno === changedRow.caseno
        ? { ...row, slotqty: newLotValue }
        : row
    );
    setTableData(newData);
  };

  const handleUpdatesItemSizes = async () => {
    try {
      setIsUpdating(true);
      await addItemsFromDetails(selectedItem, itemSizes);
    } catch (error) {
      console.error("Error updating item sizes:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const isAgentBill = docType === "AGENTBILL" || docType === "CNDN";

  return (
    <div className="w-full flex flex-col lg:flex-row gap-2 min-h-[300px] lg:min-h-[500px]">
      {/* Left panel - Item selector (hidden when pre-filled from scheme issue) */}
      {!isFromSchemeIssue && <div className="w-full lg:w-1/3">
        <Table>
          <THead>
            <TR>
              <TH compact first className="min-w-[42px]">CID</TH>
              <TH compact last>Item Name</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD compact first className="border-r border-gray-200 align-top">
                <Input
                  type="text"
                  value={rowCaseNo}
                  onChange={(e) => setRowCaseNo(e.target.value)}
                  showLabel={false}
                    className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-center"
                />
              </TD>
              <TD compact last className="border-r border-gray-200 align-top">
                <ItemSelectSizeGrouped
                  value={selectedItem}
                  onChange={handleSelectedItem}
                  vendorId={itemVendorId}
                  placeholder="Select item..."
                  requireVendor={requiresVendor}
                />
                {sizeEnabled && selectedItem && itemSizes.length > 0 ? (
                  <Table className="mt-2">
                    <THead>
                      <TR>
                        <TH compact first colSpan={2}>Item Sizes</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {itemSizes.map((itemSize, rowindex) => (
                        <TR key={`${rowindex}-${itemSize.agitemdtlId}`}>
                          <TD compact first className="border-r border-gray-200">
                            <div className="flex flex-col">
                              <span className="font-medium text-xs">
                                {itemSize.sz} {itemSize.szuom}
                              </span>
                              <span
                                className={`text-[10px] ${
                                  itemSize.packratio === 0 ? "text-red-500" : "text-gray-500"
                                }`}
                              >
                                PR: {itemSize.packratio || 0}
                              </span>
                            </div>
                          </TD>
                          <TD compact last className="border-r border-gray-200">
                            <Input
                              type="number"
                              step="0.001"
                              value={itemSize.qty}
                              onChange={(e) =>
                                handleItemRowSizeQtyChange(rowindex, e.target.value)
                              }
                              showLabel={false}
                              className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                            />
                          </TD>
                        </TR>
                      ))}
                      <TR>
                        <TD compact first colSpan={2}>
                          <button
                            type="button"
                            onClick={() => handleUpdatesItemSizes()}
                            disabled={isUpdating}
                            className="rounded bg-gray-700 px-2.5 py-1 text-xs font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isUpdating ? "Updating..." : "Update"}
                          </button>
                        </TD>
                      </TR>
                    </TBody>
                  </Table>
                ) : null}
              </TD>
            </TR>
          </TBody>
        </Table>
      </div>}

      {/* Right panel - Data table */}
      <div className={`${isFromSchemeIssue ? "w-full" : "w-full lg:w-2/3"} overflow-x-auto`}>
        <Table>
          <THead>
            <TR>
              <TH compact first className="min-w-[42px] text-center">CID</TH>
              <TH compact className="min-w-[42px] text-center">Lot</TH>
              <TH compact className="min-w-[120px]">Item Name</TH>
              <TH compact className="min-w-[60px] text-right">Qty</TH>
              {isAgentBill && <TH compact className="min-w-[65px] text-right">Rate</TH>}
              {isAgentBill && hasApplicableGst && (
                <TH compact className="min-w-[75px] text-right" title="Rate including tax">
                  Rate Incl
                </TH>
              )}
              <TH compact className="min-w-[38px] text-center">UOM</TH>
              {isAgentBill && <TH compact className="min-w-[65px] text-right">Dis Amt</TH>}
              {isAgentBill && <TH compact className="min-w-[55px] text-right">Dis %</TH>}
              {isAgentBill && <TH compact className="min-w-[75px] text-right">Amount</TH>}
              {companyHasGst && <TH compact className="min-w-[45px] text-center">HSN / SAC</TH>}
              {isAgentBill && hasApplicableGst && <TH compact className="min-w-[45px] text-right">Tax%</TH>}
              {!isFromSchemeIssue && <TH compact last className="min-w-[36px] text-center"></TH>}
            </TR>
          </THead>
          <TBody>
            {regularTableData?.map((row, itemRowIndex) => (
              <TR key={`ord-${row.ordno}-case-${row.caseno}-item-${row.item.itemid}`}>
                <TD compact first className="border-r border-gray-200 text-center">
                  <Input
                    type="text"
                    value={row?.caseno}
                    readOnly
                    isDisabled
                    showLabel={false}
                    className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-center"
                  />
                </TD>
                <TD compact className="border-r border-gray-200 text-center">
                  <Input
                    type="number"
                    value={row.slotqty || 0}
                    onChange={(e) => handleLotChange(row, e.target.value)}
                    showLabel={false}
                    className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-center"
                  />
                </TD>
                <TD compact className="border-r border-gray-200">
                  {row.item.item_full_name}
                </TD>
                <TD compact className="border-r border-gray-200">
                  <Input
                    type="number"
                    step="0.001"
                    value={row?.sqty || 0}
                    onChange={(e) => {
                      const newData = tableData?.map((r) =>
                        r.item.itemid === row.item.itemid
                          ? { ...r, sqty: e.target.value }
                          : r
                      );
                      setTableData(newData);
                    }}
                    showLabel={false}
                    className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                  />
                </TD>
                {isAgentBill && (
                  <TD compact className="border-r border-gray-200">
                    <Input
                      type="number"
                      step="0.01"
                      value={row?.srate || 0}
                      onChange={(e) => {
                        const newData = tableData?.map((r) =>
                          r.item.itemid === row.item.itemid
                            ? withSyncedRatesFromExclusive(r, e.target.value)
                            : r
                        );
                        setTableData(newData);
                      }}
                      showLabel={false}
                      className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                    />
                  </TD>
                )}
                {isAgentBill && hasApplicableGst && (
                  <TD compact className="border-r border-gray-200">
                    <Input
                      type="number"
                      step="0.01"
                      value={row?.srate_incl ?? exclusiveToInclusive(row?.srate, row?.tax)}
                      onChange={(e) => {
                        const newData = tableData?.map((r) =>
                          r.item.itemid === row.item.itemid
                            ? withSyncedRatesFromInclusive(r, e.target.value)
                            : r
                        );
                        setTableData(newData);
                      }}
                      showLabel={false}
                      className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                    />
                  </TD>
                )}
                <TD compact className="border-r border-gray-200 text-center">
                  {row.item.itemuom}
                </TD>
                {isAgentBill && (
                  <TD compact className="border-r border-gray-200">
                    <Input
                      type="number"
                      step="0.01"
                      value={row?.dis1 || 0}
                      onChange={(e) => {
                        const newDiscount = parseFloat(e.target.value);
                        const rate = parseFloat(row?.srate);
                        if (!isNaN(newDiscount) && !isNaN(rate) && newDiscount <= rate) {
                          const newData = tableData?.map((r) =>
                            r.item.itemid === row.item.itemid
                              ? withItemDirectDiscount(r, newDiscount)
                              : r
                          );
                          setTableData(newData);
                        }
                      }}
                      min={0}
                      showLabel={false}
                      className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                    />
                  </TD>
                )}
                {isAgentBill && (
                  <TD compact className="border-r border-gray-200">
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      max={99}
                      value={row?.dis2perc || 0}
                      onChange={(e) => {
                        const newData = tableData?.map((r) =>
                          r.item.itemid === row.item.itemid
                            ? withItemDiscountPercentage(r, e.target.value)
                            : r
                        );
                        setTableData(newData);
                      }}
                      showLabel={false}
                      className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                    />
                  </TD>
                )}
                {isAgentBill && (
                  <TD compact className="border-r border-gray-200 text-right">
                    {roundAmount2Dig(
                      (parseFloat(row?.sqty) || 0) *
                        getItemNetRate(row)
                    )}
                  </TD>
                )}
                {companyHasGst && (
                  <TD compact className="border-r border-gray-200 text-center">
                    {row?.item.itmhsnId}
                  </TD>
                )}
                {isAgentBill && hasApplicableGst && (
                  <TD compact className="text-right">
                    {row?.tax || 0}%
                  </TD>
                )}
                {!isFromSchemeIssue && (
                  <TD compact last className="text-center">
                    <ActionButton
                      onClick={() => removeTableItemsRow(itemRowIndex)}
                      variant="danger"
                      title="Remove row"
                      className="mr-0 !px-1.5 !py-1"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </ActionButton>
                  </TD>
                )}
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  );
}
